"use strict";
/**
 * Reducer slice for `*_SYSTEM*` actions. Owns the systems list plus the
 * cascade-cleanup that drops references from fields and workflows when a
 * system is deleted.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.systemReducer = exports.isSystemAction = void 0;
var ai_hub_settings_client_ids_1 = require("./ai-hub-settings-client-ids");
var ai_hub_settings_utils_1 = require("./ai-hub-settings-utils");
var ai_hub_settings_reducer_helpers_1 = require("./ai-hub-settings-reducer-helpers");
exports.isSystemAction = function (action) {
    switch (action.type) {
        case 'ADD_SYSTEM':
        case 'DELETE_SYSTEM':
        case 'DISCARD_SYSTEM_EDIT':
        case 'UPDATE_SYSTEM_FIELD':
        case 'UPDATE_SYSTEM_CONFIG':
        case 'SET_SELECTED_SYSTEM':
            return true;
        default:
            return false;
    }
};
var addSystem = function (state) {
    var newSystem = ai_hub_settings_utils_1.createEmptySystem();
    var newId = newSystem.__clientId;
    return __assign(__assign({}, state), { draft: __assign(__assign({}, state.draft), { systems: __spreadArrays([newSystem], state.draft.systems) }), newSystemIds: __spreadArrays([newId], state.newSystemIds), 
        // Atomic auto-select — no follow-up effect required.
        selectedSystemId: newId });
};
var deleteSystem = function (state, action) {
    var index = ai_hub_settings_client_ids_1.findSystemIndexById(state.draft.systems, action.clientId);
    if (index < 0) {
        return state;
    }
    var deletedSystem = state.draft.systems[index];
    var nextSystems = ai_hub_settings_reducer_helpers_1.removeAt(state.draft.systems, index);
    // Cascade-cleanup: drop any field mappings that referenced the
    // deleted system's `id`. Identity is intentionally `system.id`, not
    // `__clientId`, because mappings store the *user-facing* id.
    var nextFields = deletedSystem.id
        ? state.draft.fields.map(function (field) {
            var filteredMappings = field.mapping.filter(function (mapping) { return mapping.system !== deletedSystem.id; });
            return filteredMappings.length === field.mapping.length
                ? field
                : __assign(__assign({}, field), { mapping: filteredMappings });
        })
        : state.draft.fields;
    // Cascade-cleanup: also drop the deleted system's id from any
    // workflow source/target lists so the persisted config stays
    // referentially consistent.
    var nextWorkflows = deletedSystem.id
        ? state.draft.workflows.map(function (workflow) {
            var sources = workflow.sources.filter(function (entry) { return entry !== deletedSystem.id; });
            var targets = workflow.targets.filter(function (entry) { return entry !== deletedSystem.id; });
            if (sources.length === workflow.sources.length &&
                targets.length === workflow.targets.length) {
                return workflow;
            }
            return __assign(__assign({}, workflow), { sources: sources, targets: targets });
        })
        : state.draft.workflows;
    return __assign(__assign({}, state), { draft: { systems: nextSystems, workflows: nextWorkflows, fields: nextFields }, newSystemIds: state.newSystemIds.filter(function (id) { return id !== action.clientId; }), selectedSystemId: state.selectedSystemId === action.clientId ? null : state.selectedSystemId });
};
var discardSystemEdit = function (state, action) {
    var isNew = state.newSystemIds.indexOf(action.clientId) >= 0;
    var draftIndex = ai_hub_settings_client_ids_1.findSystemIndexById(state.draft.systems, action.clientId);
    if (draftIndex < 0) {
        return state;
    }
    var clearSelection = state.selectedSystemId === action.clientId ? { selectedSystemId: null } : null;
    if (isNew) {
        return __assign(__assign(__assign({}, state), (clearSelection || {})), { draft: __assign(__assign({}, state.draft), { systems: ai_hub_settings_reducer_helpers_1.removeAt(state.draft.systems, draftIndex) }), newSystemIds: state.newSystemIds.filter(function (id) { return id !== action.clientId; }) });
    }
    var savedIndex = ai_hub_settings_client_ids_1.findSystemIndexById(state.saved.systems, action.clientId);
    if (savedIndex < 0) {
        return state;
    }
    return __assign(__assign(__assign({}, state), (clearSelection || {})), { draft: ai_hub_settings_reducer_helpers_1.withSystemReplaced(state.draft, draftIndex, ai_hub_settings_utils_1.cloneAiHubSettingsSystem(state.saved.systems[savedIndex])) });
};
var updateSystemField = function (state, action) {
    var _a;
    var index = ai_hub_settings_client_ids_1.findSystemIndexById(state.draft.systems, action.clientId);
    if (index < 0) {
        return state;
    }
    return __assign(__assign({}, state), { draft: ai_hub_settings_reducer_helpers_1.withSystemReplaced(state.draft, index, __assign(__assign({}, state.draft.systems[index]), (_a = {}, _a[action.key] = action.value, _a))) });
};
var updateSystemConfig = function (state, action) {
    var index = ai_hub_settings_client_ids_1.findSystemIndexById(state.draft.systems, action.clientId);
    if (index < 0) {
        return state;
    }
    return __assign(__assign({}, state), { draft: ai_hub_settings_reducer_helpers_1.withSystemReplaced(state.draft, index, __assign(__assign({}, state.draft.systems[index]), { config: action.config })) });
};
exports.systemReducer = function (state, action) {
    switch (action.type) {
        case 'SET_SELECTED_SYSTEM':
            return state.selectedSystemId === action.clientId
                ? state
                : __assign(__assign({}, state), { selectedSystemId: action.clientId });
        case 'ADD_SYSTEM':
            return addSystem(state);
        case 'DELETE_SYSTEM':
            return deleteSystem(state, action);
        case 'DISCARD_SYSTEM_EDIT':
            return discardSystemEdit(state, action);
        case 'UPDATE_SYSTEM_FIELD':
            return updateSystemField(state, action);
        case 'UPDATE_SYSTEM_CONFIG':
            return updateSystemConfig(state, action);
        default:
            return state;
    }
};
